import { createAction, createAsyncThunk, createReducer, createSelector } from "@reduxjs/toolkit";

import { fetchWithAuth } from "~/common/fetch";
import { API_CLINICS_LIST } from "~/config";
import type { RootState } from "~/store";
import type { TClinic } from "~/types/clinic";
import type { MutableQueryState } from "~/types/redux";

const initialState: MutableQueryState<TClinic[]> = {
  status: "idle",
  data: undefined,
  error: undefined,
};

export const unlinkClinics = createAction<TClinic["clinic_id"]>("UNLINK_CLINICS_SUCCESS");

export const getClinics = createAsyncThunk("clinics/fetch", async () => {
  const response = await fetchWithAuth(API_CLINICS_LIST);

  if (!response.ok) {
    throw new Error("Error occurred trying to fetch clinics");
  }

  return response.json() as Promise<TClinic[]>;
});

const selectClinicsData = (state: RootState) => state.clinics.data;

export const selectClinics = createSelector(
  [selectClinicsData],
  (clinicsData) => clinicsData ?? [],
);

export const selectClinicsAreLoading = (state: RootState) =>
  state.clinics.data == undefined && state.clinics.status == "loading";

export const clinics = createReducer(initialState, (builder) =>
  builder
    .addCase(getClinics.pending, (state) => {
      state.status = "loading";
    })
    .addCase(getClinics.fulfilled, (state, action) => {
      state.status = "success";
      state.data = action.payload;
      state.error = undefined;
    })
    .addCase(getClinics.rejected, (state, action) => {
      state.status = "error";
      state.data = undefined;
      state.error = action.error;
    })
    .addCase(unlinkClinics, (state, action) => {
      if (state.data) {
        state.data = state.data.filter((clinic) => clinic.clinic_id != action.payload);
      }
    }),
);
